// constants/sectionPaths.ts
/**
 * Maps menu sections to concrete entry routes for landing-page resolution.
 *
 * `MAIN_SECTION_KEYS` is the priority order of landing-eligible sections (the
 * "main"-surface registry entries — never "settings"). It mirrors the backend
 * LANDING_ELIGIBLE_SECTIONS (api/constants/menu_registry.py).
 *
 * `SECTION_SUBMENU_LEAVES` maps each section's GATEABLE submenu keys (those in
 * MENU_REGISTRY) to their leaf route. The landing resolver walks these in order
 * and returns the first VISIBLE leaf — a route whose own MenuGuard passes — so a
 * "go home" redirect never bounces off a section-index route that hard-redirects
 * to a hidden submenu (e.g. /insights → /insights/chat). Dashboard has no
 * submenus and resolves to /dashboard directly.
 *
 * The leaf paths overlap SECTION_ITEMS in components/layout/SectionSubNav.tsx;
 * a drift test (utils/landingPath.test.ts) keeps the two in sync.
 */

import { MENU_REGISTRY } from "./menuRegistry";

/** Landing-eligible sections, in priority order (registry order, no settings). */
export const MAIN_SECTION_KEYS: string[] = MENU_REGISTRY.filter(
  (m) => m.surface !== "settings"
).map((m) => m.key);

export type SubmenuLeaf = { submenu: string; path: string };

export const SECTION_SUBMENU_LEAVES: Record<string, SubmenuLeaf[]> = {
  // dashboard has no submenus — resolved to /dashboard directly.
  dashboard: [],
  insights: [
    // Dashboards leads: it is the section's landing destination, and /insights
    // itself redirects here while the `insights_home` flag is off, so both
    // routes into the section agree.
    //
    // "home" is deliberately NOT a leaf. The landing resolver has no access to
    // feature flags, so listing it would send flag-off accounts to /insights
    // only to be redirected onward — and the resolver's contract is to return a
    // route that renders, not one that bounces. When the home page is released,
    // put it back at the front of this list.
    { submenu: "dashboards", path: "/insights/dashboards" },
    { submenu: "chat", path: "/insights/chat" },
    { submenu: "deep-dives", path: "/insights/deep-dives" },
    // Reports routes intentionally live at /reports (not /insights/reports):
    // the URLs predate the menu entry and delivery e-mails may link to them.
    { submenu: "reports", path: "/reports" },
    // "analyst" is deliberately NOT a leaf, for the same reason as "home":
    // the resolver cannot see feature flags, and while
    // `periodic_analysis_enabled` is off the feed is empty by construction —
    // landing an account there would drop them on a blank page. Add it here
    // when the feature is released.
  ],
  activation: [{ submenu: "segments", path: "/activation/segments" }],
  "data-management": [
    { submenu: "sources", path: "/data-management/sources" },
    { submenu: "flows", path: "/data-management/flows" },
    { submenu: "authorizations", path: "/data-management/authorizations" },
    { submenu: "destinations", path: "/data-management/destinations" },
    { submenu: "semantic-layer", path: "/data-management/semantic-layer" },
    { submenu: "glossary", path: "/data-management/glossary" },
    { submenu: "agents", path: "/data-management/agents" },
  ],
  automations: [
    { submenu: "monitors", path: "/automations" },
    { submenu: "alerts", path: "/automations/alerts" },
  ],
};

/** Always-reachable fallback (core settings are never gateable). */
export const LANDING_FALLBACK_PATH = "/settings/profile";

/** The default landing section when an account hasn't configured one. */
export const DEFAULT_LANDING_SECTION = "dashboard";
